import { SelectButton } from '@modules/queryBuilder/components/SelectButton';
import {
  MULTI_VALUE_OPERATORS,
  NO_VALUE_OPERATORS,
  USER_SINGLE_VALUE_INPUT_OPERATORS,
} from '@modules/queryBuilder/constants';
import { getFallbackDisplayNameForEqlFieldValueIfRequired } from '@modules/queryBuilder/helpers';
import { useQueryBuilderStore } from '@modules/queryBuilder/store/useQueryBuilderStore';
import { ButtonProps } from '@mui/material';
import { EqlFieldValue } from 'generated/graphql';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { NormalisedFilter } from 'v2/querybuilder/types/querystore';
import { QueryFilterValueDropdown } from './QueryFilterValueDropdown';

interface QueryFilterValueSelectButtonProps {
  filter: NormalisedFilter;
  buttonColor: ButtonProps['color'];
  readOnly: boolean;
}

export function QueryFilterValueSelectButton({
  filter,
  buttonColor,
  readOnly,
}: QueryFilterValueSelectButtonProps) {
  const eqlFieldByFieldID = useQueryBuilderStore(
    ({ metadata }) => metadata.eqlFieldByFieldID,
  );
  const eqlField =
    eqlFieldByFieldID[filter.field] ||
    eqlFieldByFieldID[
      filter.field === 'entitySentiment' ? 'entity' : filter.field
    ];
  const selectedDisplayNameByID = eqlField?.eqlFieldValueByID;
  const selectedValues: EqlFieldValue | EqlFieldValue[] = (() => {
    const getEqlFieldValue = (
      valueItem: string | number | boolean | undefined,
    ): EqlFieldValue => {
      const value = valueItem?.toString() || '';

      return (
        selectedDisplayNameByID?.[value] ?? {
          value: value,
          displayName: getFallbackDisplayNameForEqlFieldValueIfRequired({
            field: filter.field,
            value,
          }),
        }
      );
    };

    switch (filter.value.type) {
      case 'boolean':
      case 'number':
      case 'string': {
        return [getEqlFieldValue(filter.value.value)];
      }
      default:
        return filter.value.values.map((valueItem) => {
          return getEqlFieldValue(valueItem.value);
        });
    }
  })();

  const [isOpen, setIsOpen] = useState(isEmpty(selectedValues));
  const isActive = useQueryBuilderStore(({ metadata }) => metadata.isActive);
  const filterIDUnderInspect = useQueryBuilderStore(
    ({ metadata }) => metadata.filterIDUnderInspect,
  );

  useEffect(() => {
    if (filterIDUnderInspect === filter.id) {
      setIsOpen(true);
    }
  }, [filterIDUnderInspect, filter.id]);

  const shouldAllowMultipleValues = MULTI_VALUE_OPERATORS.includes(
    filter.operator,
  );

  const shouldUseInputValues = USER_SINGLE_VALUE_INPUT_OPERATORS.includes(
    filter.operator,
  );

  const isNLS = filter.field === 'search';

  const shouldShowError = (() => {
    if (isNLS) {
      return (
        !isActive &&
        filter.value.type === 'string[]' &&
        !filter.value.values.length
      );
    }
    return (
      !isActive &&
      (shouldAllowMultipleValues ? !selectedValues.length : !selectedValues[0])
    );
  })();

  const getLabel = () => {
    return shouldAllowMultipleValues
      ? selectedValues
          .map((selectedValue) => selectedValue.displayName)
          .join(', ')
      : selectedValues[0]?.displayName ?? '';
  };

  const getNLSLabel = () => {
    if (filter.value.type === 'string[]') {
      return filter.value.values.map((value) => value.value).join(', ');
    }
    return '';
  };

  const value = shouldAllowMultipleValues
    ? selectedValues
    : selectedValues[0] || null;

  const shouldHideValue =
    filter.field === 'feedback' || NO_VALUE_OPERATORS.includes(filter.operator);

  if (shouldHideValue) {
    return null;
  }

  return (
    <SelectButton
      className="qb-filter-value-select-button"
      isOpen={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      label={isNLS ? getNLSLabel() : getLabel()}
      placeholder={
        shouldAllowMultipleValues ? 'Select values' : 'Select a value'
      }
      componentsProps={{
        popper: { sx: { width: 560 } },
        button: {
          color: shouldShowError ? 'error' : undefined,
          sx: {
            maxWidth: 250,
          },
        },
      }}
      buttonColor={buttonColor}
      readOnly={readOnly}
      renderDropDownComponent={(props) => (
        <QueryFilterValueDropdown
          {...props}
          id={`qb-filter-value-dropdown-${filter.id}`}
          filter={filter}
          shouldUseInputValues={shouldUseInputValues}
          isNLS={isNLS}
          selectedValues={selectedValues}
          shouldAllowMultipleValues={shouldAllowMultipleValues}
          value={value}
        />
      )}
    />
  );
}
